import { startOfDay } from 'date-fns';
import { Client } from '@/api/getClient';
import { ClientConstraints } from '@/hooks/providers/useClientConstraintsProvider';
import { isWithdrawFlowDone } from '@/utils/withdrawFlow';
import type { LatestLoan } from '@/hooks/providers/useLatestLoanProvider';

// Compares day-level dates (ignoring time) so the option is disabled only before the earliest allowed payment day
export const isMinMonthlyRepaymentDisabled = (
  earliestMtpPaymentDate?: string,
) => {
  if (!earliestMtpPaymentDate) {
    return false;
  }

  const currentDate = startOfDay(new Date());
  const earliestPaymentDate = startOfDay(new Date(earliestMtpPaymentDate));

  return currentDate < earliestPaymentDate;
};

// Finds the fee entry whose term and unit match the loan's current term, indicating an active extension offer
export const findActiveLoanExtension = (data?: LatestLoan) => {
  if (!data?.loanExtensionFees) {
    return undefined;
  }

  return data?.loanExtensionFees?.find(
    (value) =>
      value.term === data?.loanTerm.value &&
      value.unit === data?.loanTerm.termUnit,
  );
};

export const isAdditionalAmountDisabled = (
  latestLoan?: LatestLoan | null,
  client?: Client | null | undefined,
  constraints?: ClientConstraints | null,
) => {
  const topUpAmount = latestLoan?.topUpAmount || 0;
  const openPrincipalAmount = client?.openPrincipalAmount || 0;
  // Net top-up headroom available after subtracting what is already open
  const topUpValue = topUpAmount - openPrincipalAmount;
  const additionalAmountValue = client?.creditLimit ?? 0;

  // Disabled when no additional amount flag, or the credit limit leaves no room beyond one step
  const additionalAmountDisabled =
    !client?.additionalAmount ||
    isWithdrawFlowDone(latestLoan) ||
    additionalAmountValue <=
      topUpValue + (constraints?.amountInterval.step ?? 0);

  return additionalAmountDisabled;
};
